import { supabase } from './supabase';
import { postJournal } from './journal';
import { ACCOUNTS } from './accounts';

let _lkrCurrencyId = null;
async function lkrCurrencyId() {
  if (_lkrCurrencyId) return _lkrCurrencyId;
  const { data } = await supabase.from('currencies').select('id').eq('code', 'LKR').single();
  _lkrCurrencyId = data?.id;
  return _lkrCurrencyId;
}

// A client's current unapplied balance in Client Deposits (2010) — money received with
// no invoice to apply it to yet (a pro forma deposit, or an advance taken with nothing
// raised at all). This is a client-level pool, not tied to any specific document.
export async function getClientDepositBalance(clientId) {
  const { data } = await supabase
    .from('journal_lines')
    .select('base_debit, base_credit, journal_entries!inner(status)')
    .eq('account_id', ACCOUNTS.CLIENT_DEPOSITS)
    .eq('party_type', 'client')
    .eq('party_id', clientId)
    .eq('journal_entries.status', 'POSTED');
  return (data || []).reduce((s, l) => s + (parseFloat(l.base_credit) || 0) - (parseFloat(l.base_debit) || 0), 0);
}

// A client's current net AR position (1030). Positive means the client still owes us on
// open invoices; negative means they've paid more than they owe — that overpayment is a
// refundable amount, separate from the Client Deposits pool above.
export async function getClientArBalance(clientId) {
  const { data } = await supabase
    .from('journal_lines')
    .select('base_debit, base_credit, journal_entries!inner(status)')
    .eq('account_id', ACCOUNTS.AR)
    .eq('party_type', 'client')
    .eq('party_id', clientId)
    .eq('journal_entries.status', 'POSTED');
  return (data || []).reduce((s, l) => s + (parseFloat(l.base_debit) || 0) - (parseFloat(l.base_credit) || 0), 0);
}

// Moves `amount` out of a client's Client Deposits balance and applies it against a
// specific invoice's AR — a real, reversible journal entry (DR Client Deposits / CR AR),
// not just a relabeling. Used both for the explicit staff-triggered "apply deposit" action
// and for auto-carrying a deposit that was already linked to the pro forma this invoice
// came from (that intent was given at receipt time, so no extra confirmation needed).
export async function applyClientDeposit(clientId, invoiceId, amount, username, description) {
  const amt = parseFloat(amount) || 0;
  if (amt <= 0) return null;
  const currency_id = await lkrCurrencyId();
  return postJournal({
    date: new Date().toISOString().slice(0, 10),
    description,
    reference_type: 'DEPOSIT_APPLICATION',
    reference_id: invoiceId,
    currency_id,
    exchange_rate: 1,
    lines: [
      { account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: amt, credit_amount: 0, description, party_type: 'client', party_id: clientId },
      { account_id: ACCOUNTS.AR,              debit_amount: 0,   credit_amount: amt, description, party_type: 'client', party_id: clientId },
    ],
    created_by: username,
  });
}

// Called right after an invoice is created from a pro forma/quotation. Any deposit that
// was explicitly linked to that exact quotation at receipt time carries straight through —
// staff already gave that intent, so this applies automatically (capped at the invoice
// total; any excess stays in the client's deposit pool for the popup to offer elsewhere).
export async function applyQuotationDepositToInvoice(quotationId, invoiceId, clientId, invoiceTotalAmount, username) {
  const { data: receipts } = await supabase
    .from('receipt_vouchers')
    .select('voucher_number, base_amount, amount')
    .eq('reference_type', 'quotation')
    .eq('reference_id', quotationId)
    .is('voided_at', null);
  if (!receipts?.length) return null;

  const totalDeposit = receipts.reduce((s, r) => s + (parseFloat(r.base_amount) || parseFloat(r.amount) || 0), 0);
  const amount = Math.min(totalDeposit, parseFloat(invoiceTotalAmount) || 0);
  if (amount <= 0) return null;

  const voucherList = receipts.map(r => r.voucher_number).join(', ');
  return applyClientDeposit(clientId, invoiceId, amount, username, `Pro forma deposit applied (${voucherList})`);
}

// Client's original opening-balance AR amount (from the OB journal entry), or 0 if none.
export async function getOpeningBalance(clientId) {
  // Sum every live opening-balance line, not just the first: a client can carry more than
  // one (e.g. after merging duplicate client records), and reading only one understated
  // the old balance that FIFO allocation pays off first. Reversed entries and the
  // reversals themselves are skipped, same as the ledgers.
  const { data } = await supabase
    .from('journal_lines')
    .select('debit_amount, credit_amount, journal_entries!inner(description, status, is_reversed, reversal_of)')
    .eq('party_type', 'client')
    .eq('party_id', clientId)
    .eq('journal_entries.status', 'POSTED')
    .eq('journal_entries.is_reversed', false)
    .is('journal_entries.reversal_of', null)
    .ilike('journal_entries.description', 'Opening balance%');
  const total = (data || []).reduce((s, l) => s + (parseFloat(l.debit_amount) || 0) - (parseFloat(l.credit_amount) || 0), 0);
  return Math.max(0, total);
}

// How much of a client's opening balance has already been paid off via allocations.
// Counts receipt allocations plus any Client Deposits applied to the old balance
// (DEPOSIT_APPLICATION with no invoice reference — see applyClientDeposit with invoiceId null).
export async function getOpeningBalancePaid(clientId) {
  const [{ data }, { data: depLines }] = await Promise.all([
    supabase
      .from('receipt_allocations')
      .select('amount')
      .eq('client_id', clientId)
      .eq('target_type', 'old_balance'),
    supabase
      .from('journal_lines')
      .select('credit_amount, journal_entries!inner(reference_type, reference_id, status, is_reversed, reversal_of)')
      .eq('account_id', ACCOUNTS.AR)
      .eq('party_type', 'client')
      .eq('party_id', clientId)
      .eq('journal_entries.reference_type', 'DEPOSIT_APPLICATION')
      .is('journal_entries.reference_id', null)
      .eq('journal_entries.status', 'POSTED')
      .eq('journal_entries.is_reversed', false)
      .is('journal_entries.reversal_of', null),
  ]);
  const allocTotal = (data || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const depTotal = (depLines || []).reduce((s, l) => s + (parseFloat(l.credit_amount) || 0), 0);
  return allocTotal + depTotal;
}

// Total paid against one invoice — sums new-style allocations, any legacy receipt_vouchers
// rows that reference this invoice directly and have no allocations (so we never double-count
// a voucher that's been migrated to allocations), plus any Client Deposits already applied
// straight to this invoice's AR (applyClientDeposit posts a journal only, no receipt_allocations
// row — omitting this made FIFO allocation blind to money already settled this way, so a
// second receipt could get allocated against an invoice that was already partly/fully cleared).
export async function getInvoicePaidAmount(invoiceId) {
  const [{ data: allocs }, { data: legacyRvs }, { data: depositApps }] = await Promise.all([
    supabase.from('receipt_allocations').select('amount').eq('invoice_id', invoiceId),
    supabase.from('receipt_vouchers')
      .select('id, base_amount, amount')
      .eq('reference_type', 'invoice').eq('reference_id', invoiceId).is('voided_at', null),
    supabase.from('journal_entries')
      .select('journal_lines(credit_amount, account_id)')
      .eq('reference_type', 'DEPOSIT_APPLICATION').eq('reference_id', invoiceId)
      .eq('status', 'POSTED').eq('is_reversed', false),
  ]);
  const allocatedVoucherIds = new Set();
  if (legacyRvs?.length) {
    const { data: linkedAllocs } = await supabase
      .from('receipt_allocations').select('receipt_voucher_id')
      .in('receipt_voucher_id', legacyRvs.map(r => r.id));
    (linkedAllocs || []).forEach(a => allocatedVoucherIds.add(a.receipt_voucher_id));
  }
  const allocTotal = (allocs || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const legacyTotal = (legacyRvs || [])
    .filter(r => !allocatedVoucherIds.has(r.id))
    .reduce((s, r) => s + (parseFloat(r.base_amount) || parseFloat(r.amount) || 0), 0);
  const depositTotal = (depositApps || []).reduce((s, je) =>
    s + (je.journal_lines || []).filter(l => l.account_id === ACCOUNTS.AR).reduce((s2, l) => s2 + (parseFloat(l.credit_amount) || 0), 0), 0);
  return allocTotal + legacyTotal + depositTotal;
}

function invoiceTotal(inv) {
  return (inv.line_items || []).reduce((s, li) =>
    s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
}

/**
 * Computes a FIFO allocation of `amount` across a client's outstanding debt:
 * opening balance first (oldest), then open invoices oldest-issued first.
 * Any amount left over after everything is cleared becomes an "advance".
 * Returns [{ target_type, invoice_id, label, amount }]
 */
export async function computeFifoAllocation(clientId, amount, excludeInvoiceIds = []) {
  let remaining = parseFloat(amount) || 0;
  const allocations = [];
  if (remaining <= 0) return allocations;

  const [obTotal, obPaid, { data: invoices }] = await Promise.all([
    getOpeningBalance(clientId),
    getOpeningBalancePaid(clientId),
    supabase.from('invoices').select('id, invoice_number, issued_date, line_items')
      .eq('client_id', clientId).neq('status', 'VOID').order('issued_date', { ascending: true }),
  ]);

  const obOutstanding = Math.max(0, obTotal - obPaid);
  if (obOutstanding > 0 && remaining > 0) {
    const apply = Math.min(obOutstanding, remaining);
    allocations.push({ target_type: 'old_balance', invoice_id: null, label: 'Old Balance (opening balance)', amount: apply });
    remaining -= apply;
  }

  // Look up paid amounts in parallel batches: checking one invoice at a time took a
  // long-standing client (dozens of paid invoices before the first open one) tens of
  // seconds, which looked like the Apply button doing nothing.
  const candidates = (invoices || []).filter(inv => !excludeInvoiceIds.includes(inv.id) && invoiceTotal(inv) > 0);
  const paidById = {};
  for (let i = 0; i < candidates.length; i += 10) {
    const batch = candidates.slice(i, i + 10);
    const paids = await Promise.all(batch.map(inv => getInvoicePaidAmount(inv.id)));
    batch.forEach((inv, k) => { paidById[inv.id] = paids[k]; });
  }

  for (const inv of candidates) {
    if (remaining <= 0) break;
    const total = invoiceTotal(inv);
    const paid = paidById[inv.id] ?? await getInvoicePaidAmount(inv.id);
    const outstanding = Math.max(0, total - paid);
    if (outstanding <= 0) continue;
    const apply = Math.min(outstanding, remaining);
    allocations.push({ target_type: 'invoice', invoice_id: inv.id, label: inv.invoice_number, amount: apply });
    remaining -= apply;
  }

  if (remaining > 0.01) {
    allocations.push({ target_type: 'advance', invoice_id: null, label: 'Unallocated advance', amount: remaining });
  }

  return allocations;
}
